<template>
  <div>
    <popup-hint hint="inventoryreceipts">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Inventory receipts">
          <template v-slot:left v-if="receiptStatus.draft+receiptStatus.pending">
            <list-notification v-model="showStatus">
              <v-list-item v-if="receiptStatus.draft">
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{receiptStatus.draft}} draft inventory receipt{{receiptStatus.draft===1?'':'s'}}
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.from=null;filters.to=null;filters.supplierId=null;filters.search=null;filters.paymentMethod='';filters.pending='0';filters.draft='1'})">View</v-btn>
              </v-list-item>
              <v-list-item v-if="receiptStatus.pending">
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{receiptStatus.pending}} invoice receipt{{receiptStatus.pending>1?'s':''}} pending
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.from=null;filters.to=null;filters.supplierId=null;filters.search=null;filters.paymentMethod='';filters.pending='1',filters.draft='0'})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Inventory receipts are used to record inventory additions resulting from purchases from suppliers.
      </template>
    </popup-hint>

    <purtrak-table name="receipts" objectType="inventory receipts" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="inventoryReceipts2" v-model="selected"
                   fields="id,date,due,billNumber,vendorId,vendorName,privateNote,total,currency,exchangeRate,draft,
                           qbo,paymentMethod,paymentMethodId,pendingReview,attachmentURL"
                   :listFilters="listFilters"
                   :totals.sync="totals" totalFields="totalAmount"
                   :item-class="receipt => (receipt.draft ? 'grey--text ' : (receipt.pendingReview ? 'warning--text' : ''))"
                   @dblclick="receipt => $router.push({name:'PurchasingInventoryReceiptsEdit',params:{inventoryReceiptId:receipt.id}})"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   title="Inventory receipts"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.from?dateDisplay:'',
                                           filters.draft=='1'?'Draft':'',
                                           filterPaymentName,filters.supplierId?filters.supplierName:'',filters.pending=='1'?'Pending only':'']}"
                   download-endpoint="inventoryReceiptsExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.from=null;filters.to=null;filters.paymentId='';filters.pending='0';filters.draft='0',filters.search='';filters.customerId=null">
          <v-list-item>
            <filter-search :label="[{text:'Supplier',value:'vendorName'},{text:'Number',value:'number'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all hide-details/>
          </v-list-item>
          <v-list-item dense>
            <v-checkbox v-model="filters.draft" true-value="1" false-value="0" label="Show draft only"/>
          </v-list-item>
          <template v-if="connectedToAccounting">
            <v-list-item>
              <v-autocomplete label="Payment method" dense outlined :items="paymentMethods" item-value="id" item-text="name" v-model="filters.paymentId" hide-details/>
            </v-list-item>
            <v-list-item dense>
              <v-checkbox v-model="filters.pending" true-value="1" false-value="0" label="Show pending only"/>
            </v-list-item>
          </template>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search='';filters.customerId=null">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.draft=='1'" close small class="ml-1" @click:close="filters.draft='0'">Draft only</v-chip>
          <v-chip v-show="filters.paymentId!=''" close small class="ml-1" @click:close="filters.paymentId=''">Pay by: {{filterPaymentName}}</v-chip>
          <v-chip v-show="filters.supplierId" close small class="ml-1" @click:close="filters.supplierId=null">{{filters.supplierName}}</v-chip>
          <v-chip v-show="filters.pending=='1'" close small class="ml-1" @click:close="filters.pending='0'">Pending only</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'PurchasingInventoryReceiptsEditNew'}"><v-list-item-title>Create inventory receipt</v-list-item-title></v-list-item>
        </template>
        <template v-if="company.allowRecurringTransactions">
          <v-divider/>
          <v-list-item :to="{name:'RecurringTransactions',query:{targetType:'Receipt'}}"><v-list-item-title>Recurring inventory receipts</v-list-item-title></v-list-item>
        </template>
      </template>
      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" @click="deleteMultiple=true;showDelete=true">Batch delete</v-btn>
      </template>
      <template v-slot:item.date="{item,printing}">
        {{item.date | formatDateTime}}
        <div v-if="!printing && item.draft" class="caption black--text">Draft</div>
      </template>
      <template v-slot:item.draft="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.due="{value}">
        {{value | formatDate}}
      </template>
      <template v-slot:item.pendingReview="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.qbo="{item,printing}">
        <template v-if="printing">
          {{item.pendingReview ? 'Pending' : (item.qbo?'Y':'')}}
        </template>
        <span v-else :class="{'warning--text':item.pendingReview}">{{item.pendingReview ? 'Pending' : (item.qbo?'Y':'')}}</span>
      </template>
      <template v-slot:item.vendorName="{item,printing}">
        {{item.vendorName}}
        <div v-if="!printing" class="text-caption">{{item.privateNote}}</div>
      </template>
      <template v-slot:item.total="{item,printing}">
        {{item.currency===homeCurrency?'':item.currency}} {{item.total | currency}}
        <div v-if="!printing && item.currency!==homeCurrency" class="text-caption">{{item.total*item.exchangeRate | currency}}</div>
      </template>
      <template v-slot:header.attachmentURL>
        <v-icon>mdi-paperclip</v-icon>
      </template>
      <template v-slot:item.attachmentURL="{value}">
        <v-icon v-if="value" class="green--text" @click="open(value)">mdi-paperclip</v-icon>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'PurchasingInventoryReceiptsEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Inventory receipt operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <template>
              <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Inventory receipts',to:{name:'PurchasingInventoryReceipts'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:'',forward:1}}"><v-list-item-title>Trace lot</v-list-item-title></v-list-item>
              <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
              <v-divider/>
              <v-list-item :to="{name:'PurchasingInventoryReceiptsEdit',params:{inventoryReceiptId:item.id}}"><v-list-item-title>Edit inventory receipt</v-list-item-title></v-list-item>
              <v-list-item @click="updatePrivateNote(item)"><v-list-item-title>Update private note</v-list-item-title></v-list-item>
              <v-list-item @click="inventoryReceipt=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete inventory receipt</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4" :class="expansionPanelClass">
          <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:70%">
            <thead>
              <tr>
                <th :class="expansionPanelClass" width="35%">Description</th>
                <th :class="expansionPanelClass" width="15%">Lot</th>
                <th class="text-right" :class="expansionPanelClass" width="20%">Quantity</th>
                <th class="text-right" :class="expansionPanelClass" width="15%" v-if="values">Unit price</th>
                <th class="text-right" :class="expansionPanelClass" width="15%" v-if="values">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line of item.lines" :key="line.key">
                <td class="text-caption">{{line.name}}</td>
                <td class="text-caption">
                  <a @click="$router.push({name:'ReportsView',params:{back:[{text:'Inventory receipts',to:{name:'PurchasingInventoryReceipts'}}]},query:{to:'reports/inventory/ReportsLot',id:line.bulkContainerId || line.id,lot:line.lot,forward:1}})">{{line.lot}}</a>
                </td>
                <td class="text-caption text-right">{{line.quantity | number(line.precision)}} {{line.units}}</td>
                <td class="text-caption text-right" v-if="values">{{line.unitPrice | currency}}</td>
                <td class="text-caption text-right" v-if="values">{{line.amount | currency}}</td>
              </tr>
              <template v-for="tax of item.taxLines">
                <tr :key="tax.id">
                  <td class="text-caption text-right" colspan="4">
                    {{tax.name}} @ {{tax.percent}}% on {{tax.onAmount | currency}}
                  </td>
                  <td class="text-caption text-right">{{tax.amount | currency}}</td>
                </tr>
              </template>
            </tbody>
          </v-simple-table>
          </v-card>
        </td>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='total' && values">
              {{totals.totalAmount | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
    </purtrak-table>

    <component :is="dialog" v-bind="dialogProperties" @done="dialog = null"/>
    <delete-object v-model="showDelete" :name="deleteMultiple?'ALL selected inventory receipts':'this inventory receipt'" @delete="performDelete(inventoryReceipt.id)"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'

export default {
  name: 'PurchasingInventoryReceipts',
  mixins: [performDelete],
  data() {
    return {
      showStatus: false,
      filters: {
        pending: '0',
        from: null,
        to: null,
        draft: '0',
        supplierId: null,
        paymentId: '',
        search: '',
        searchField: 'vendorName',
      },
      receiptStatus: {pending:0, draft:0},
      totals: {totalAmount:0},
      dateDisplay: '',
      searchDisplay: '',
      paymentMethods: [{id:'',name:'All'},{divider:true},{id:'bill',name:'Bill'}],
      inventoryReceipt: {},
      showDelete: false,
      deleteMultiple: false,
      dialog: null,
      dialogProperties: {},
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'vendorName', visible:true},
                       { value: 'total', visible:true},
                       { value: 'qbo', visible:true},
                       { value: 'paymentMethod', visible:true},
                       { value: 'billNumber', visible:false},
                       { value: 'attachmentURL', visible:true,excludeFromExport:true},
                       { value: 'due', visible:false},
                       { value: 'draft', visible:false},
                       { value: 'pendingReview', visible:false}],
      headers: [{ text: 'DATE', value: 'date', width:'15%',sortable:true},
                { text: 'SUPPLIER', value: 'vendorName' ,width:'40%',sortable:true},
                { text: 'TOTAL', value: 'total' ,width:'15%',sortable:true,align:'right', omit:() => !this.values},
                { text: 'QBO', value: 'qbo' ,width:'10%',sortable:false,align:'center',omit:() => !this.connectedToAccounting},
                { text: 'METHOD', value: 'paymentMethod' ,width:'15%',sortable:true},
                { text: 'NUMBER', value: 'billNumber' ,width:'10%',sortable:true},
                { text: 'DUE', value: 'due' ,width:'15%',sortable:true},
                { text: 'ATTACHMENT', value: 'attachmentURL' ,width:'5%',sortable:false,print:false},
                { text: 'DRAFT?', value: 'draft' ,width:'10%',sortable:true,align:'center'},
                { text: 'PENDING', value: 'pendingReview' ,width:'10%',sortable:true}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    homeCurrency() {
      return this.company.settings.homeCurrency;
    },
    readOnly() {
      return !this.company.permissions.includes('buying.write');
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    values() {
      return this.company.permissions.includes('values');
    },
    filterPaymentName() {
      return this.paymentMethods.find(p => p.id === this.filters.paymentId)?.name;
    },
    listFilters() {
      const filters = [];
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.supplierId !== null)
        filters.push({field:'vendorId', comparator:'eq', value:this.filters.supplierId});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});
      if (this.filters.draft == '1')
        filters.push({field:'draft', comparator:'eq', value:'true'});
      if (this.filters.pending == '1')
        filters.push({field:'pendingReview', comparator:'eq', value:'true'});
      if (this.filters.paymentId) {
        if (this.filters.paymentId === 'none')
          filters.push({field:'qbo', comparator:'eq', value:'false'});
        else
          filters.push({field:'qbo', comparator:'eq', value:'true'},{field:'paymentMethodId', comparator:'eq', value:this.filters.paymentId === 'bill' ? '' : this.filters.paymentId});
      }

      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
    await this.$lazyListLoad(this.paymentMethods, 'paymentMethods2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}])
    this.paymentMethods.push({divider:true},{id:'none',name:'None'});

    await this.getStatus();
    if (!['PurchasingInventoryReceiptsEdit', 'PurchasingInventoryReceiptsEditNew'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.receiptStatus.draft+this.receiptStatus.pending && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    async getStatus() {
      this.loading = true;
      const results = await this.$apiCall(`query PurchasingInventoryReceipts {
                                                inventoryReceiptsStatus {
                                                  draft,pending
                                                }
                                              }`);
      if (!results.error)
        this.receiptStatus = results.data.inventoryReceiptsStatus;
      this.loading = false;
    },
    async expand(item, value) {
      if (value && !item.lines) {
        this.loading = true;
        const results = await this.$apiCall(`query PurchasingInventoryReceipts($id:ID!) {
                                                inventoryReceipt(id:$id) {
                                                  lines{name,id,bulkContainerId,quantity,units,precision,unitPrice,amount,lot}
                                                  taxLines {id,name,amount,onAmount,percent}
                                                }
                                              }`,
                                            {id:item.id});
        this.$set(item, 'lines', results.data.inventoryReceipt.lines);
        this.$set(item, 'taxLines', results.data.inventoryReceipt.taxLines);
        let key = 1;
        item.lines.forEach(line => line.key = key++);
        this.loading = false;
      }
    },
    open(url) {
      const win = window.open(url);
      if (!win) {
        this.$store.commit('setError', {type:'warning',message:`Your browser permissions must allow pop-up windows`});
        return;
      }
    },
    updatePrivateNote(inventoryReceipt) {
      this.dialogProperties = {item:inventoryReceipt, operation:'inventoryReceiptUpdatePrivateNote'};
      this.dialog = () => import(`@/components/UpdatePrivateNote`);
    }
  }
}
</script>